import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../Drawer";
import { SelectDropdown } from "../SelectDropdown";
import { OverlayGrayBackground } from "../OverlayGrayBackground";
import { LoaderSpinner } from "../LoaderSpinner";
import { getAllowedUserPermissions } from "../../helpers/common";
import { CHILDREN_TAB_FORECAST_PLANNING_SYSTEM } from "../../consts/management/users/users";
import { getReportCategories } from "../../slices/quickbooks/reportCategories";
import { getProfitAndLossTables, ProfitAndLossNewLoadingKeys } from "../../slices/quickbooks/profitAndLossNewSlice";
import {
    fetchForecastSettings,
    ForecastSettingsLoadingKeys,
    saveForecastSettings,
} from "../../slices/forecastSettingsSlice";

const TABLE_LABELS = {
    summary: "Summary",
    "revenue-breakdown": "Revenue Breakdown",
    "contribution-margin": "Contribution Margin",
    "total-cost-of-goods-sold": "Total Cost of Goods Sold",
    "total-operating-expenses": "Total Operating Expenses",
    "net-income-summary": "Net Income Summary",
};

const emptyLocalSettings = {
    revenue_category_id: 1,
    adSpend_category_id: 3,
    metrics: {
        revenue: null,
        contribution_margin: null,
        net_income: null,
    },
};

export const ForecastSystemSettingsSidebar = ({
    isOpen,
    onClose,
    marginFromSidebar,
}) => {
    const dispatch = useDispatch();
    const { currentUser, loading: authLoading } = useSelector((state) => state.auth);
    const { categories: reportCategories } = useSelector((state) => state.reportCategories);
    const {
        profitAndLossTables,
        loadingKeys: profitAndLossLoadingKeys,
    } = useSelector((state) => state.profitAndLossNew);
    const {
        forecastSettings,
        loadingKeys: forecastSettingsLoadingKeys,
    } = useSelector((state) => state.forecastSettings);

    const userPermissions = currentUser?.permissions || [];
    const { childIdToAccess } = getAllowedUserPermissions({ permissions: userPermissions });
    const routesAccess = childIdToAccess.get(CHILDREN_TAB_FORECAST_PLANNING_SYSTEM);
    const hasEditAccess = routesAccess === "edit";

    const [localSettings, setLocalSettings] = useState(emptyLocalSettings);

    useEffect(() => {
        if (!isOpen || !hasEditAccess) return;
        dispatch(fetchForecastSettings());
        dispatch(getReportCategories({}));
        dispatch(getProfitAndLossTables({}));
    }, [dispatch, hasEditAccess, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setLocalSettings(emptyLocalSettings);
            return;
        }

        setLocalSettings({
            revenue_category_id: forecastSettings?.revenue_category_id ?? 1,
            adSpend_category_id: forecastSettings?.adSpend_category_id ?? 3,
            metrics: {
                revenue: forecastSettings?.metrics?.revenue ?? null,
                contribution_margin: forecastSettings?.metrics?.contribution_margin ?? null,
                net_income: forecastSettings?.metrics?.net_income ?? null,
            },
        });
    }, [forecastSettings, isOpen]);

    const categoryOptions = useMemo(() => {
        return (reportCategories || []).map((category) => ({
            label: category?.name || `Category ${category?.id}`,
            value: category?.id,
        }));
    }, [reportCategories]);

    const profitAndLossItemOptions = useMemo(() => {
        const preferredOrder = [
            "summary",
            "revenue-breakdown",
            "contribution-margin",
            "total-cost-of-goods-sold",
            "total-operating-expenses",
            "net-income-summary",
        ];

        const sortedTableKeys = Object.keys(profitAndLossTables || {}).sort((a, b) => {
            const aIndex = preferredOrder.indexOf(a);
            const bIndex = preferredOrder.indexOf(b);
            if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
            if (aIndex === -1) return 1;
            if (bIndex === -1) return -1;
            return aIndex - bIndex;
        });

        return sortedTableKeys.flatMap((tableKey) =>
            (profitAndLossTables?.[tableKey] || []).map((tableItem) => ({
                label: `${TABLE_LABELS[tableKey] || tableKey} / ${tableItem?.item?.name || `Item ${tableItem?.id}`}`,
                value: tableItem?.id,
            }))
        );
    }, [profitAndLossTables]);

    const isFetching =
        forecastSettingsLoadingKeys.includes(ForecastSettingsLoadingKeys.fetch) ||
        profitAndLossLoadingKeys.includes(ProfitAndLossNewLoadingKeys.getProfitAndLossTables);
    const isSaving = forecastSettingsLoadingKeys.includes(ForecastSettingsLoadingKeys.save);

    const canSave = Boolean(
        localSettings?.revenue_category_id &&
        localSettings?.adSpend_category_id &&
        localSettings?.metrics?.revenue &&
        localSettings?.metrics?.contribution_margin &&
        localSettings?.metrics?.net_income
    );

    const handleSave = async () => {
        if (!canSave) return;
        await dispatch(saveForecastSettings(localSettings)).unwrap();
        onClose?.();
    };

    if (authLoading) {
        return (
            <Drawer isOpen={isOpen} onClose={onClose} title="Forecast Settings" width="900px" size="xl">
                <div className="p-6">
                    <LoaderSpinner colorSpinner="black" width="70" height="70" border="3" />
                </div>
            </Drawer>
        );
    }

    if (!hasEditAccess) {
        return (
            <Drawer isOpen={isOpen} onClose={onClose} title="Forecast Settings" width="900px" size="xl">
                <div className="p-6">No access</div>
            </Drawer>
        );
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title="Forecast Settings"
            showActionButtons={true}
            submitButtonText={isSaving ? "Saving..." : "Save"}
            submitDisabled={!canSave || isSaving}
            onSubmit={handleSave}
            width="900px"
            size="xl"
            marginFromSidebar={marginFromSidebar}
        >
            <div className="relative px-6 py-4">
                {isFetching && <OverlayGrayBackground />}
                <div className="space-y-6">
                    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <h3 className="text-base font-semibold">Budget Categories</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Choose which budget categories should drive forecast revenue and ad spend totals.
                        </p>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Revenue Category
                                </label>
                                <SelectDropdown
                                    options={categoryOptions}
                                    value={localSettings?.revenue_category_id}
                                    onChange={(value) => {
                                        setLocalSettings((prev) => ({
                                            ...prev,
                                            revenue_category_id: value,
                                        }));
                                    }}
                                    selectClassname="!bg-white"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Ad Spend Category
                                </label>
                                <SelectDropdown
                                    options={categoryOptions}
                                    value={localSettings?.adSpend_category_id}
                                    onChange={(value) => {
                                        setLocalSettings((prev) => ({
                                            ...prev,
                                            adSpend_category_id: value,
                                        }));
                                    }}
                                    selectClassname="!bg-white"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                        <h3 className="text-base font-semibold">P&amp;L Metrics</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Choose the Profit &amp; Loss rows that should be used for actual revenue, contribution margin, and net income.
                        </p>
                        <div className="mt-4 grid grid-cols-1 gap-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Revenue Metric
                                </label>
                                <SelectDropdown
                                    options={profitAndLossItemOptions}
                                    value={localSettings?.metrics?.revenue}
                                    onChange={(value) => {
                                        setLocalSettings((prev) => ({
                                            ...prev,
                                            metrics: {
                                                ...prev.metrics,
                                                revenue: value,
                                            },
                                        }));
                                    }}
                                    selectClassname="!bg-white"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Contribution Margin Metric
                                </label>
                                <SelectDropdown
                                    options={profitAndLossItemOptions}
                                    value={localSettings?.metrics?.contribution_margin}
                                    onChange={(value) => {
                                        setLocalSettings((prev) => ({
                                            ...prev,
                                            metrics: {
                                                ...prev.metrics,
                                                contribution_margin: value,
                                            },
                                        }));
                                    }}
                                    selectClassname="!bg-white"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-gray-700">
                                    Net Income Metric
                                </label>
                                <SelectDropdown
                                    options={profitAndLossItemOptions}
                                    value={localSettings?.metrics?.net_income}
                                    onChange={(value) => {
                                        setLocalSettings((prev) => ({
                                            ...prev,
                                            metrics: {
                                                ...prev.metrics,
                                                net_income: value,
                                            },
                                        }));
                                    }}
                                    selectClassname="!bg-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};